import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShiftDetailPanel = ({ shift, onBook, onClose, conflicts = [] }) => {
  if (!shift) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center py-12">
          <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            Schicht auswählen
          </h3>
          <p className="text-muted-foreground">
            Wählen Sie eine Schicht aus der Liste, um Details anzuzeigen.
          </p>
        </div>
      </div>
    );
  }

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`)?.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDepartmentLabel = (department) => {
    const labels = {
      'customer-service': 'Kundendienst',
      'operations': 'Betrieb',
      'manufacturing': 'Fertigung',
      'administration': 'Verwaltung',
      'maintenance': 'Wartung',
      'security': 'Sicherheit'
    };
    return labels?.[department] || department;
  };

  const hasConflicts = conflicts?.length > 0;

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">Schicht Details</h3>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          iconName="X"
        />
      </div>
      <div className="p-4 space-y-6">
        {/* Basic Information */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Grundinformationen</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Datum:</span>
              <span className="text-sm font-medium text-foreground">
                {formatDate(shift?.date)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Zeit:</span>
              <span className="text-sm font-medium text-foreground">
                {formatTime(shift?.startTime)} - {formatTime(shift?.endTime)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Dauer:</span>
              <span className="text-sm font-medium text-foreground">
                {shift?.duration} Stunden
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Abteilung:</span>
              <span className="text-sm font-medium text-foreground">
                {getDepartmentLabel(shift?.department)}
              </span>
            </div>
          </div>
        </div>

        {/* Location & Contact */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Standort & Kontakt</h4>
          <div className="space-y-3">
            <div>
              <span className="text-sm text-muted-foreground block mb-1">Standort:</span>
              <div className="flex items-start space-x-2">
                <Icon name="MapPin" size={16} className="text-muted-foreground mt-0.5" />
                <span className="text-sm text-foreground">
                  {shift?.location || 'Hauptgebäude, Etage 2, Raum 201'}
                </span>
              </div>
            </div>
            <div>
              <span className="text-sm text-muted-foreground block mb-1">Vorgesetzter:</span>
              <div className="flex items-center space-x-2">
                <Icon name="User" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">
                  {shift?.supervisor || 'Maria Schmidt'}
                </span>
              </div>
            </div>
            <div>
              <span className="text-sm text-muted-foreground block mb-1">Kontakt:</span>
              <div className="flex items-center space-x-2">
                <Icon name="Phone" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">
                  {shift?.supervisorPhone || '+49 30 12345678'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Special Instructions */}
        {shift?.instructions && (
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Besondere Anweisungen</h4>
            <div className="bg-muted rounded-lg p-3">
              <p className="text-sm text-foreground">
                {shift?.instructions}
              </p>
            </div>
          </div>
        )}

        {/* Requirements & Benefits */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Anforderungen & Vorteile</h4>
          <div className="space-y-2">
            {shift?.isPriority && (
              <div className="flex items-center space-x-2 text-amber-700">
                <Icon name="Star" size={16} />
                <span className="text-sm font-medium">Prioritäts-Schicht für Senioren</span>
              </div>
            )}
            {shift?.hasCrossTraining && (
              <div className="flex items-center space-x-2 text-emerald-700">
                <Icon name="GraduationCap" size={16} />
                <span className="text-sm font-medium">Weiterbildungsmöglichkeit verfügbar</span>
              </div>
            )}
            {shift?.requirements && (
              <div className="flex items-start space-x-2">
                <Icon name="CheckCircle" size={16} className="text-muted-foreground mt-0.5" />
                <span className="text-sm text-foreground">{shift?.requirements}</span>
              </div>
            )}
          </div>
        </div>

        {/* Conflict Check Results */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Konfliktprüfung</h4>
          {hasConflicts ? (
            <div className="space-y-2">
              {conflicts?.map((conflict, index) => (
                <div key={index} className="flex items-start space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertTriangle" size={16} className="text-error mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-error">Konflikt erkannt</p>
                    <p className="text-sm text-error/80">{conflict?.message}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center space-x-2 p-3 bg-success/10 border border-success/20 rounded-lg">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-sm font-medium text-success">
                Keine Konflikte gefunden - Buchung möglich
              </span>
            </div>
          )}
        </div>

        {/* Integration Status */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">System Status</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Lohnsystem:</span>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span className="text-sm text-success">Synchronisiert</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Zeiterfassung:</span>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span className="text-sm text-success">Verbunden</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-4 border-t border-border">
          <div className="flex space-x-3">
            <Button
              variant="default"
              onClick={() => onBook(shift?.id)}
              disabled={hasConflicts}
              iconName="Calendar"
              iconPosition="left"
              className="flex-1"
            >
              {hasConflicts ? 'Konflikt vorhanden' : 'Schicht buchen'}
            </Button>
            <Button
              variant="outline"
              iconName="Download"
              iconSize={16}
            >
              Export
            </Button>
          </div>
          
          {hasConflicts && (
            <p className="text-xs text-muted-foreground mt-2">
              Lösen Sie die Konflikte, um diese Schicht zu buchen.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShiftDetailPanel;